import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  FolderKanban,
  BookOpen,
  Heart,
  MoreHorizontal,
  ArrowLeft,
  Timer,
  Lightbulb,
  Sun,
  Settings,
  User,
  MessageCircle,
  Users,
  CircleDot,
  Trophy,
  BarChart3,
  Activity,
} from 'lucide-react';
import { WaveformLogo } from '../icons';
import { ThemeToggle } from '../ui';
import { UserMenu } from '../UserMenu';
import './AppLayout.css';

interface AppLayoutProps {
  children: React.ReactNode;
  /** Hide the bottom navigation (for pages that have their own nav like Dashboard) */
  hideBottomNav?: boolean;
  /** Show back button with optional custom back path */
  showBackButton?: boolean;
  backPath?: string;
  backLabel?: string;
  /** Callback for back button (overrides backPath navigation) */
  onBack?: () => void;
  /** Page title for header */
  pageTitle?: string;
}

const navItems = [
  { path: '/dashboard', icon: Home, label: 'Home' },
  { path: '/projects', icon: FolderKanban, label: 'Works' },
  { path: '/morning-pages', icon: BookOpen, label: 'Pages' },
  { path: '/more', icon: Heart, label: 'Blocked?' },
];

const moreMenuItems = [
  { path: '/checkin', icon: Sun, label: 'Daily Check-in' },
  { path: '/timer', icon: Timer, label: 'Flow Timer' },
  { path: '/ideas', icon: Lightbulb, label: 'Capture Ideas' },
  { path: '/coach', icon: MessageCircle, label: 'Creative Coach' },
  { path: '/partners', icon: Users, label: 'Partners' },
  { path: '/circles', icon: CircleDot, label: 'Circles' },
  { path: '/challenges', icon: Trophy, label: 'Challenges' },
  { path: '/pulse', icon: Activity, label: 'Creative Pulse' },
  { path: '/analytics', icon: BarChart3, label: 'Analytics' },
  { path: '/settings', icon: Settings, label: 'Settings' },
  { path: '/profile', icon: User, label: 'Profile' },
];

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  hideBottomNav = false,
  showBackButton = false,
  backPath,
  backLabel,
  onBack,
  pageTitle,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const isMoreActive = moreMenuItems.some((item) => isActive(item.path));

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  // Close more menu on navigation
  useEffect(() => {
    setShowMoreMenu(false);
  }, [location.pathname]);

  return (
    <div className="app-layout">
      {/* Top Header */}
      <header className="app-layout__header">
        <div className="app-layout__header-left">
          {showBackButton ? (
            <button className="app-layout__back-btn" onClick={handleBack}>
              <ArrowLeft size={20} />
              <span>{backLabel || 'Back'}</span>
            </button>
          ) : (
            <Link to="/dashboard" className="app-layout__logo">
              <WaveformLogo size={28} />
              <span className="app-layout__logo-text">Kadans</span>
            </Link>
          )}
        </div>

        {pageTitle && (
          <h1 className="app-layout__page-title">{pageTitle}</h1>
        )}

        <div className="app-layout__header-right">
          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      {/* Main Content */}
      <main className="app-layout__main">
        {children}
      </main>

      {/* Bottom Navigation */}
      {!hideBottomNav && (
        <nav className="app-layout__bottom-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`app-layout__bottom-nav-item ${isActive(item.path) ? 'app-layout__bottom-nav-item--active' : ''}`}
            >
              <item.icon size={22} />
              <span>{item.label}</span>
            </Link>
          ))}

          {/* More button with popup menu */}
          <div className="app-layout__more-wrapper" ref={moreMenuRef}>
            <button
              className={`app-layout__bottom-nav-item app-layout__more-btn ${isMoreActive || showMoreMenu ? 'app-layout__bottom-nav-item--active' : ''}`}
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              aria-label="More"
            >
              <MoreHorizontal size={22} />
              <span>More</span>
            </button>

            {showMoreMenu && (
              <div className="app-layout__more-menu">
                {moreMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`app-layout__more-menu-item ${isActive(item.path) ? 'app-layout__more-menu-item--active' : ''}`}
                  >
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>
      )}
    </div>
  );
};

export default AppLayout;
